/* eslint-disable no-underscore-dangle, camelcase */
import { aperioPlanKind } from './aperioPlanCopy';
import {
  APERIO_TRACKING_IDS,
  AperioTrackingIds,
  NEXTDOOR_EVENTS,
  OPENAI_EVENTS,
} from './aperioTrackingConfig';
import { whenIdle } from './idleLoad';

/**
 * Aperio booking attribution and conversion tracking (shared tracking
 * contract with aperiomobility.com).
 *
 * - Reads the site's `apr_consent`, `apr_attr` and `apr_cta` cookies
 *   (Domain=.aperiomobility.com), and writes `apr_attr` itself for a visitor
 *   who lands on the portal directly from an ad, when consent allows.
 * - Loads the vendor tags (GA4 / Google Ads with Consent Mode v2, Meta,
 *   Microsoft UET, Nextdoor, LinkedIn, the OpenAI Ads pixel) from the public ids in
 *   aperioTrackingConfig.ts - only with consent, and only for ids that are
 *   set (every vendor but LinkedIn since 2026-09-25; LinkedIn's Insight Tag
 *   since 2026-09-26, page view only until a conversion id exists).
 * - Fires the contract's snake_case events (booking_start, address_*,
 *   vehicle_identified, plan_select, appointment_selected, begin_checkout,
 *   purchase, phone_click) next to the existing Mixpanel events.
 * - Builds the `attribution` argument createWorkOrder sends to the server.
 *
 * Inert for every tenant but the Aperio preset: until initAperioTracking()
 * runs (index.tsx, only for that preset) every export is a no-op that
 * touches no cookie, loads no script and sends nothing.
 *
 * Ad platforms never get the address, VIN, plate, GPS or photos: only event
 * names, value / currency, the transaction id, the booking skin / A/B arm
 * (Meta custom data), and - for purchase, with advertising consent - the
 * email / phone for enhanced conversions.
 */

// ---------------------------------------------------------------- types

/** One touch of `apr_attr`: UTMs (s,m,c,t,n), click ids, landing path, referrer. */
export interface AttributionTouch {
  s?: string;
  m?: string;
  c?: string;
  t?: string;
  n?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  li_fat_id?: string;
  ndclid?: string;
  oppref?: string;
  lp?: string;
  ref?: string;
  ts?: number;
}

export interface AttributionCookie {
  v: 1;
  f: AttributionTouch;
  l: AttributionTouch;
}

export interface AperioConsent {
  analytics: boolean;
  ads: boolean;
  /** The visitor made (or was implied to make) a choice on the site. */
  hasCookie: boolean;
  gpc: boolean;
  /** How that choice was made: on the banner (`explicit`) or by the region rule (`implied`). */
  mode?: 'explicit' | 'implied' | null;
}

/**
 * How the portal knew the consent it sends with a booking
 * (booking_attribution.consent_state): a choice on the banner, the implied
 * grant of a region that does not ask first, no decision yet, or Global
 * Privacy Control.
 */
export type ConsentState = 'explicit' | 'implied' | 'none' | 'gpc';

/** What the landing URL said, read before the app strips any param. */
export interface LandingSnapshot {
  touch: AttributionTouch;
  /** The URL carried a utm_* or click id. */
  campaign: boolean;
}

/** Mirrors the server's BookingAttributionInput. */
export interface BookingAttributionVariables {
  firstSource?: string;
  firstMedium?: string;
  firstCampaign?: string;
  firstTerm?: string;
  firstContent?: string;
  lastSource?: string;
  lastMedium?: string;
  lastCampaign?: string;
  lastTerm?: string;
  lastContent?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  liFatId?: string;
  ndclid?: string;
  /** OpenAI (ChatGPT) Ads click reference; sent only when there is one. */
  oppref?: string;
  fbp?: string;
  fbc?: string;
  gaClientId?: string;
  mpDeviceId?: string;
  landingPath?: string;
  firstLandingPath?: string;
  referrerHost?: string;
  ctaId?: string;
  analyticsConsent: boolean;
  adConsent: boolean;
  /** How the two flags were known (a diagnostic; never a personal value). */
  consentState?: ConsentState;
  /** Booked from one of our own devices (the internal flag); sent only when true. */
  internal?: boolean;
}

// ------------------------------------------------------------- constants

const COOKIE_CONSENT = 'apr_consent';
const COOKIE_ATTR = 'apr_attr';
const COOKIE_CTA = 'apr_cta';
const ATTR_MAX_AGE_S = 90 * 24 * 60 * 60;
const SITE_DOMAIN = 'aperiomobility.com';

const UTM_KEYS: Array<[string, keyof AttributionTouch]> = [
  ['utm_source', 's'],
  ['utm_medium', 'm'],
  ['utm_campaign', 'c'],
  ['utm_term', 't'],
  ['utm_content', 'n'],
];

export const CLICK_ID_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'ndclid',
  // The click reference an OpenAI (ChatGPT) Ads click carries (2026-10-20).
  // Not stripped from the URL by the hand-off, so OpenAI's own pixel reads it too.
  'oppref',
] as const;

const MAX_VALUE_LENGTH = 255;
const PURCHASE_SENT_KEY = 'apr_purchase_sent';
const QA_KEY = 'apr_qa';
const QA_SOURCE = /^(qa\d*|test)$/i;
/** The two hosts one GA4 session spans (gtag `linker`). */
export const LINKER_DOMAINS = ['aperiomobility.com', 'book.aperiomobility.com'];
const BOOKING_START_KEY = 'apr_booking_start';

// ----------------------------------------------------------------- state

type Params = Record<string, string | number | boolean | null | undefined>;

interface TrackingState {
  enabled: boolean;
  ids: AperioTrackingIds;
  consent: AperioConsent;
  attr: AttributionCookie | null;
  landing: LandingSnapshot | null;
  sent: Set<string>;
  /** Our own testing (the QA flag, readQaFlag). */
  qa: boolean;
  /**
   * Our own device (the internal flag, readInternalFlag): a signed-in
   * staff account, or the team's `?apr_internal=1` link.
   */
  internal: boolean;
  /**
   * Added to every GA4 event: which booking skin the visitor is on and the
   * A/B arm (consumer/skin.ts). Empty until the skin is known.
   */
  context: Params;
}

const initialState = (): TrackingState => ({
  enabled: false,
  ids: APERIO_TRACKING_IDS,
  consent: {
    analytics: false, ads: false, hasCookie: false, gpc: false,
  },
  attr: null,
  landing: null,
  sent: new Set(),
  qa: false,
  internal: false,
  context: {},
});

let state: TrackingState = initialState();

/** The document-level phone_click listener is installed once per page. */
let phoneListenerInstalled = false;

/** For tests: back to the inert state. */
export function resetAperioTracking(): void {
  state = initialState();
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  staffSeen = false;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  rememberConsentValue(null);
}

/**
 * The booking skin and A/B arm (consumer/skin.ts), sent with every GA4
 * event and as custom data on every Meta event from now on - the purchase
 * included - so start -> booked can be compared by skin. Never a personal
 * value.
 */
export function setTrackingContext(context: {
  skin?: string | null;
  abArm?: string | null;
}): void {
  state.context = {
    ...(context.skin && { skin: context.skin }),
    ...(context.abArm && { ab_arm: context.abArm }),
  };
}

export function isAperioTrackingEnabled(): boolean {
  return state.enabled;
}

// --------------------------------------------------------------- helpers

type AnyWindow = Window & Record<string, any>;
const win = (): AnyWindow | null => (
  typeof window === 'undefined' ? null : (window as unknown as AnyWindow)
);

/** The same host test as public/index.html's inline Aperio scripts. */
export function isAperioHost(host?: string): boolean {
  const h = host ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  return /(^|\.)aperiomobility\.com$/i.test(h) || /^aperio\./i.test(h);
}

const clean = (value: unknown, max = MAX_VALUE_LENGTH): string | undefined => {
  if (value === null || value === undefined) return undefined;
  // eslint-disable-next-line no-control-regex
  const text = String(value).replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return text ? text.slice(0, max) : undefined;
};

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  if (!found) return null;
  try {
    return decodeURIComponent(found.slice(prefix.length));
  } catch {
    return found.slice(prefix.length);
  }
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const onSite = host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`);
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ];
  if (onSite) parts.push(`Domain=.${SITE_DOMAIN}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
}

/** `v1.a<0|1>.d<0|1>.<e|i>.<unixSeconds>` → the two decisions, or null. */
export function parseConsentCookie(
  raw: string | null,
): { analytics: boolean; ads: boolean } | null {
  const match = /^v1\.a([01])\.d([01])\.([ei])\.(\d+)$/.exec((raw || '').trim());
  if (!match) return null;
  return { analytics: match[1] === '1', ads: match[2] === '1' };
}

/**
 * The decision the portal's own consent bar made on this page
 * (util/portalConsent writeConsentCookie), kept in memory as well, so it
 * holds for this page view when the browser refuses the cookie. Never
 * stored anywhere else.
 */
let rememberedConsent: string | null = null;

/** For util/portalConsent: the `apr_consent` value just written (null forgets it). */
export function rememberConsentValue(value: string | null): void {
  rememberedConsent = value && parseConsentCookie(value) ? value : null;
}

/** The decision on file: the shared cookie, else this page's own (cookies refused). */
export function currentConsentValue(): string | null {
  const cookie = readCookie(COOKIE_CONSENT);
  if (parseConsentCookie(cookie)) return cookie;
  return rememberedConsent;
}

const consentModeOf = (raw: string | null): 'explicit' | 'implied' | null => {
  const match = /\.([ei])\.\d+$/.exec((raw || '').trim());
  if (!match) return null;
  return match[1] === 'i' ? 'implied' : 'explicit';
};

const hasGpc = (): boolean => {
  try {
    return (navigator as any)?.globalPrivacyControl === true;
  } catch {
    return false;
  }
};

/**
 * The visitor's consent as the portal applies it (privacy policy: "nothing
 * is loaded, stored or sent until you accept" where the law asks first;
 * implied elsewhere): the decision on file - the site's `apr_consent`
 * cookie, explicit or implied, or the portal bar's own (util/portalConsent,
 * which writes the implied grant as soon as /geo.json says the region does
 * not ask first). With NO decision on file nothing is granted: not
 * analytics, not advertising, and no `apr_attr` is written - the visitor
 * may be in an ask-first region that has not answered yet. Global Privacy
 * Control always denies advertising and, without a decision on file,
 * analytics too (a refusal everywhere).
 *
 * Until 2026-10-24 "no decision" counted as analytics ON and advertising
 * OFF, so every booking made before the bar had resolved the region (and
 * every one made before the portal had a bar) was recorded with
 * ad_consent false: no Google / Meta / Microsoft booking conversion.
 */
export function readConsent(): AperioConsent {
  const raw = currentConsentValue();
  const cookie = parseConsentCookie(raw);
  const gpc = hasGpc();
  return {
    analytics: !!cookie && cookie.analytics,
    ads: !!cookie && cookie.ads && !gpc,
    hasCookie: !!cookie,
    gpc,
    mode: cookie ? consentModeOf(raw) : null,
  };
}

/** What the booking says about how its consent was known (ConsentState). */
export function consentStateOf(consent: AperioConsent): ConsentState {
  if (consent.hasCookie && consent.mode) {
    return consent.gpc && consent.mode === 'implied' ? 'gpc' : consent.mode;
  }
  return consent.gpc ? 'gpc' : 'none';
}

/**
 * Mixpanel on an Aperio host must stay off when the visitor declined
 * analytics on the site, or sends Global Privacy Control.
 */
export function aperioAnalyticsDeclined(): boolean {
  const cookie = parseConsentCookie(currentConsentValue());
  return (!!cookie && !cookie.analytics) || hasGpc();
}

/**
 * The visitor ACCEPTED analytics on the site (`apr_consent` a1, explicit or
 * implied) and sends no Global Privacy Control. This, not the absence of a
 * decline, is what lets Mixpanel keep its identity in the cookie shared with
 * the site (util/analytics mixpanelPersistence).
 */
export function aperioAnalyticsGranted(): boolean {
  const cookie = parseConsentCookie(currentConsentValue());
  return !!cookie && cookie.analytics && !hasGpc();
}

// ------------------------------------------------------- the QA flag

/**
 * Our own testing, kept out of the numbers (GA4 2026-09-15..10-12: three QA
 * purchases from utm_source "qa2" were counted as revenue). The marketing
 * site and this portal share one first-party flag, `apr_qa=1` on
 * .aperiomobility.com (localStorage keeps a copy where the cookie is
 * refused): a visit with `?qa=1`, or a utm_source of qa, qa2, qa3 ... or test,
 * sets it for 90 days; `?qa=0` clears it. While it is set: GA4 hits carry
 * traffic_type=internal, no `purchase` reaches GA4 (a `qa_purchase` without a
 * value does, for checking), no advertising tag loads or receives anything,
 * the OpenAI pixel would log (debug), and the booking's attribution says
 * lastSource "qa" so the server sends no conversion for it
 * (server ConversionsService.isTestBooking).
 */
export function readQaFlag(search?: string): boolean {
  const w = win();
  if (!w) return false;
  const store = (on: boolean) => {
    const host = w.location.hostname;
    const onSite = host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`);
    const attrs = `; Path=/; SameSite=Lax${onSite ? `; Domain=.${SITE_DOMAIN}` : ''}${
      w.location.protocol === 'https:' ? '; Secure' : ''}`;
    try {
      document.cookie = `${QA_KEY}${on ? '=1; Max-Age=7776000' : '=; Max-Age=0'}${attrs}`;
    } catch {
      // cookies blocked
    }
    try {
      if (on) w.localStorage.setItem(QA_KEY, '1');
      else w.localStorage.removeItem(QA_KEY);
    } catch {
      // storage blocked
    }
  };
  try {
    const params = new URLSearchParams(search ?? w.location.search);
    if (params.get('qa') === '0') {
      store(false);
      return false;
    }
    if (params.get('qa') === '1' || QA_SOURCE.test(params.get('utm_source') || '')) {
      store(true);
      return true;
    }
  } catch {
    // no URL API: fall through to what is stored
  }
  if (readCookie(QA_KEY) === '1') return true;
  try {
    return w.localStorage.getItem(QA_KEY) === '1';
  } catch {
    return false;
  }
}

export function isQaTraffic(): boolean {
  return state.qa;
}

// ------------------------------------------------- the internal flag

const INTERNAL_KEY = 'apr_internal';
const INTERNAL_PARAM = 'apr_internal';
const INTERNAL_MAX_AGE_S = 365 * 24 * 60 * 60;

/**
 * The roles that work for us (server utils/roles FRANCHISE_ROLES): a
 * device signed in with one of them is ours.
 */
export const INTERNAL_ROLES = ['admin', 'manager', 'technician', 'sales', 'tester'];

/** The account holds a staff / admin role. */
export function isStaffRoles(roles: unknown): boolean {
  return Array.isArray(roles) && roles.some(
    (r) => typeof r === 'string' && INTERNAL_ROLES.includes(r),
  );
}

/**
 * A staff sign-in or a `?apr_internal=1` seen on this page load, kept in
 * memory until it may be stored: the link's parameter is gone from the URL
 * after the first route change, long before an accept on a later step.
 */
let staffSeen = false;

function storeInternal(on: boolean): void {
  const w = win();
  if (!w) return;
  const host = w.location.hostname;
  const onSite = host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`);
  const attrs = `; Path=/; SameSite=Lax${onSite ? `; Domain=.${SITE_DOMAIN}` : ''}${
    w.location.protocol === 'https:' ? '; Secure' : ''}`;
  try {
    document.cookie = `${INTERNAL_KEY}${on ? `=1; Max-Age=${INTERNAL_MAX_AGE_S}` : '=; Max-Age=0'}${attrs}`;
  } catch {
    // cookies blocked
  }
  try {
    if (on) w.localStorage.setItem(INTERNAL_KEY, '1');
    else w.localStorage.removeItem(INTERNAL_KEY);
  } catch {
    // storage blocked
  }
}

const internalStored = (): boolean => {
  if (readCookie(INTERNAL_KEY) === '1') return true;
  try {
    return win()?.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Our own devices, kept out of the numbers like the QA flag (owner,
 * 2026-10-25): any visit by a signed-in staff / admin account, and the dev
 * team's opt-in link `?apr_internal=1` (`?apr_internal=0` clears it; a
 * later staff sign-in sets it again). The marketing site reads and honours
 * the same first-party flag, `apr_internal=1` on .aperiomobility.com
 * (365 days; localStorage keeps a copy).
 *
 * While it is on: Mixpanel `internal: true` + `traffic_type: internal` (still
 * tracked, so boards can filter), GA4 `traffic_type: internal`, no
 * advertising tag loads or receives anything, and the booking tells the
 * server (attribution `internal`), which sends no conversion for it.
 *
 * Like `apr_attr`, the flag is only STORED once the visitor allows
 * analytics or advertising (an ask-first region before an accept, a
 * decline and GPC store nothing): until then it holds for this page view,
 * in memory. Clearing it is always allowed.
 */
export function readInternalFlag(search?: string): boolean {
  const w = win();
  if (!w) return false;
  let param: string | null = null;
  try {
    param = new URLSearchParams(search ?? w.location.search).get(INTERNAL_PARAM);
  } catch {
    param = null;
  }
  if (param === '0') {
    staffSeen = false;
    if (internalStored()) storeInternal(false);
    return false;
  }
  if (param === '1') staffSeen = true;
  return staffSeen || internalStored();
}

export function isInternalTraffic(): boolean {
  return state.internal;
}

/** QA or internal: our own traffic, which no report or ad platform may count. */
export function isOwnTraffic(): boolean {
  return state.qa || state.internal;
}

/** Writes the flag once the visitor's consent allows a first-party cookie of ours. */
function persistInternal(): void {
  if (!state.internal || internalStored()) return;
  if (state.consent.analytics || state.consent.ads) storeInternal(true);
}

// ------------------------------------------------ vendor cookies (QA #33)

/**
 * The first-party cookies each category's tags leave behind - the same list
 * as the marketing site's consent.js. Matched by name only, so our own
 * `apr_*` / `mp_*` cookies are never touched here.
 */
export const VENDOR_COOKIE_PATTERNS: Record<'analytics' | 'ads', RegExp[]> = {
  analytics: [/^_ga$/, /^_ga_/, /^_gid$/, /^_gat(?:$|_)/],
  ads: [
    /^_gcl_/, /^_fbp$/, /^_fbc$/, /^_uetsid(?:_exp)?$/, /^_uetvid(?:_exp)?$/, /^_uetmsclkid$/,
    /^MUID$/, /^ndp_/, /^ndclid$/, /^li_/, /^_li_/, /^__oppref$/, /^__oaiq/,
  ],
};

/**
 * The Domain values a cookie for `host` may carry: none (host-only), the host
 * itself and each parent domain (`.aperiomobility.com` for the portal, which
 * is where gtag's "auto" domain, Meta and UET write theirs).
 */
export function cookieDomainVariants(host: string): string[] {
  const h = (host || '').toLowerCase();
  const out = [''];
  if (!h || /^[\d.]+$/.test(h) || !h.includes('.')) return out;
  const parts = h.split('.');
  for (let i = 0; i < parts.length - 1; i += 1) out.push(`; Domain=.${parts.slice(i).join('.')}`);
  return out;
}

/**
 * Deletes the vendor cookies (and the UET / Google Ads localStorage copies)
 * of every category the visitor's consent denies: advertising when
 * `apr_consent` says d0, when there is no decision on file, or under GPC;
 * analytics when it says a0. A decline on the marketing site therefore
 * cleans up on the portal too, from its first page view. Returns the
 * deleted cookie names.
 */
export function purgeVendorCookies(consent: AperioConsent = readConsent()): string[] {
  if (typeof document === 'undefined') return [];
  const patterns = [
    ...(consent.analytics ? [] : VENDOR_COOKIE_PATTERNS.analytics),
    ...(consent.ads ? [] : VENDOR_COOKIE_PATTERNS.ads),
  ];
  if (!patterns.length) return [];
  const hit = (name: string) => patterns.some((re) => re.test(name));
  const names = Array.from(new Set(document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n && hit(n))));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const tail = `=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT${secure}`;
  const domains = cookieDomainVariants(window.location.hostname);
  names.forEach((n) => domains.forEach((d) => {
    try {
      document.cookie = `${n}${tail}${d}`;
    } catch {
      // cookies blocked: nothing was stored either
    }
  }));
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && hit(k)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // storage blocked
  }
  return names;
}

// UTF-8 <-> the byte string btoa/atob work on, without the deprecated
// escape()/unescape() (QA 2026-09-25 #52). Same bytes as before.
const utf8Bytes = (text: string): string => encodeURIComponent(text)
  .replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
const fromUtf8Bytes = (bytes: string): string => decodeURIComponent(
  Array.from(bytes, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
);

const toBase64Url = (text: string): string => btoa(utf8Bytes(text))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

const fromBase64Url = (text: string): string => {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return fromUtf8Bytes(atob(padded));
};

export function encodeAttr(attr: AttributionCookie): string {
  return toBase64Url(JSON.stringify(attr));
}

export function decodeAttr(raw: string | null): AttributionCookie | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(raw));
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      v: 1,
      f: parsed.f && typeof parsed.f === 'object' ? parsed.f : {},
      l: parsed.l && typeof parsed.l === 'object' ? parsed.l : {},
    };
  } catch {
    return null;
  }
}

/** Referrer host, only when the visitor came from another host. */
function externalReferrer(referrer: string, host: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const refHost = new URL(referrer).hostname.toLowerCase();
    return refHost && refHost !== host.toLowerCase() ? clean(refHost) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * What the landing URL says about the visit: UTMs, click ids, path (never
 * its query string) and external referrer. Pure: reads, writes nothing.
 * Must run before the app strips the hand-off params (index.tsx).
 */
export function snapshotLanding(
  loc: { search: string; pathname: string; hostname: string } = window.location,
  referrer: string = typeof document !== 'undefined' ? document.referrer : '',
  now: number = Date.now(),
): LandingSnapshot {
  const params = new URLSearchParams(loc.search || '');
  const touch: AttributionTouch = {};
  let campaign = false;
  UTM_KEYS.forEach(([param, key]) => {
    const value = clean(params.get(param));
    if (value) {
      (touch as any)[key] = value;
      campaign = true;
    }
  });
  CLICK_ID_KEYS.forEach((key) => {
    const value = clean(params.get(key), 512);
    if (value) {
      touch[key] = value;
      campaign = true;
    }
  });
  touch.lp = clean(loc.pathname || '/', 512);
  const ref = externalReferrer(referrer, loc.hostname || '');
  if (ref) touch.ref = ref;
  touch.ts = Math.floor(now / 1000);
  return { touch, campaign };
}

/**
 * The attribution cookie after this landing: first touch set once, last
 * touch replaced only when the landing carries a utm_* or click id.
 */
export function mergeAttribution(
  existing: AttributionCookie | null,
  landing: LandingSnapshot,
): AttributionCookie {
  if (!existing) {
    return { v: 1, f: landing.touch, l: landing.touch };
  }
  return {
    v: 1,
    f: existing.f && Object.keys(existing.f).length ? existing.f : landing.touch,
    l: landing.campaign ? landing.touch : existing.l,
  };
}

/** GA4 client id from the `_ga` cookie (`GA1.1.<random>.<ts>` → `<random>.<ts>`). */
export function gaClientIdFromCookie(raw: string | null): string | undefined {
  const match = /^GA\d+\.\d+\.(\d+\.\d+)$/.exec((raw || '').trim());
  return match ? match[1] : undefined;
}

/**
 * Meta's click id cookie, or one built from an `fbclid` on the landing URL
 * the way the pixel builds it (`fb.1.<ms>.<fbclid>`) when there is none yet.
 */
function metaFbc(): string | undefined {
  const cookie = clean(readCookie('_fbc'), 512);
  if (cookie) return cookie;
  const fbclid = state.landing?.touch.fbclid || state.attr?.l?.fbclid;
  const ts = state.landing?.touch.fbclid
    ? state.landing.touch.ts
    : state.attr?.l?.ts;
  return fbclid ? `fb.1.${(ts || Math.floor(Date.now() / 1000)) * 1000}.${fbclid}` : undefined;
}

// --------------------------------------------------------- vendor loaders

// The vendors' queues are set up at once; their scripts are fetched after
// the first paint (util/idleLoad: load + idle, or the first interaction), so
// ~300 KB of tags no longer compete with the booking step (QA 2026-09-25 #14).
function appendScript(script: HTMLScriptElement): void {
  whenIdle(() => {
    if (!document.querySelector(`script[data-apr-src="${script.src}"]`)) {
      document.head.appendChild(script);
    }
  });
}

function loadScript(src: string): void {
  const w = win();
  if (!w || document.querySelector(`script[data-apr-src="${src}"]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  script.setAttribute('data-apr-src', src);
  appendScript(script);
}

// Advertising: the visitor's consent, and never our own traffic (the QA
// flag, the internal flag).
const adConsent = (): boolean => state.consent.ads && !state.qa && !state.internal;
/**
 * A vendor's analytics tag (GA4): only once the visitor has made a consent
 * choice on the site - `apr_consent` on file, explicit or the site's
 * implied grant - and it allows analytics. Before any choice nothing
 * loads or fires, as on the site, whose tags wait for AperioConsent
 * (owner launch check 2026-10-21, item 10: tags fired before any choice).
 */
const analyticsTagConsent = (): boolean => (
  state.consent.hasCookie && state.consent.analytics
);
const gtagOn = (): boolean => {
  const { ids } = state;
  return (!!ids.ga4Id && analyticsTagConsent())
    || (!!ids.googleAdsId && adConsent());
};
const ga4On = (): boolean => !!state.ids.ga4Id && analyticsTagConsent();
const adsOn = (): boolean => !!state.ids.googleAdsId && adConsent();
const metaOn = (): boolean => !!state.ids.metaPixelId && adConsent();
const uetOn = (): boolean => !!state.ids.uetTagId && adConsent();
const nextdoorOn = (): boolean => !!state.ids.nextdoorPixelId && adConsent();
const linkedinOn = (): boolean => !!state.ids.linkedinPartnerId && adConsent();
const openaiOn = (): boolean => !!state.ids.openaiPixelId && adConsent();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(...args: unknown[]): void {
  const w = win();
  if (!w) return;
  w.dataLayer = w.dataLayer || [];
  // gtag.js reads the `arguments` object, exactly as Google's snippet pushes it.
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

/**
 * The landing page as GA4's first page_view should see it: this page's path
 * with only the campaign keys of the URL the visitor arrived on (utm_* and
 * the click ids). The hand-off strips those from the address bar before
 * gtag.js arrives, and the root route used to drop them, so GA4 saw the
 * portal's first page without its campaign. Never an address, name, email
 * or phone: every other parameter is left out.
 */
export function landingPageLocation(
  href: string = typeof window !== 'undefined' ? window.location.href : '',
): string {
  try {
    const url = new URL(href);
    const keep = new URLSearchParams();
    const campaignKeys = [...UTM_KEYS.map(([param]) => param), ...CLICK_ID_KEYS];
    url.searchParams.forEach((value, key) => {
      if (campaignKeys.includes(key as any) && value) keep.append(key, value.slice(0, 512));
    });
    const query = keep.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return '';
  }
}

/** GA4's internal-traffic marker, on our own traffic only. */
const ownTrafficParams = (): Record<string, string> => (
  state.qa || state.internal ? { traffic_type: 'internal' } : {}
);

/** The Google click ids gtag's conversion linker stores. */
const GOOGLE_CLICK_IDS = ['gclid', 'gbraid', 'wbraid'] as const;

/**
 * `location` with the landing's campaign keys (utm_* and click ids, from
 * the snapshot taken before the app stripped them) when it carries none of
 * its own. Only this page load's landing: an older campaign in `apr_attr`
 * is not this session's source.
 */
export function withLandingCampaign(
  location: string,
  landing: LandingSnapshot | null,
): string {
  if (!location || !landing?.campaign) return location;
  try {
    const url = new URL(location);
    const campaignKeys = [...UTM_KEYS.map(([param]) => param), ...CLICK_ID_KEYS];
    if (campaignKeys.some((key) => url.searchParams.has(key))) return location;
    UTM_KEYS.forEach(([param, key]) => {
      const value = clean(landing.touch[key]);
      if (value) url.searchParams.set(param, value);
    });
    CLICK_ID_KEYS.forEach((key) => {
      const value = clean(landing.touch[key], 512);
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  } catch {
    return location;
  }
}

/**
 * The cookie gtag's conversion linker keeps each Google click id in (as
 * the real gtag.js writes them, headless check 2026-10-25): gclid ->
 * `_gcl_aw` (`GCL.<ts>.<gclid>`), wbraid -> `_gcl_gb` (`GCL.<ts>.<wbraid>`),
 * gbraid -> `_gcl_ag` (`2.1.k<gbraid>$i<ts>`).
 */
const GCL_COOKIE: Record<typeof GOOGLE_CLICK_IDS[number], string> = {
  gclid: '_gcl_aw',
  wbraid: '_gcl_gb',
  gbraid: '_gcl_ag',
};

/**
 * The Google click ids this visit is known by (the landing snapshot, else
 * `apr_attr`'s last touch) that gtag's conversion linker cannot see: not on
 * the page URL `href`, and not in its cookie yet. Empty when there is
 * nothing to hand over.
 */
export function missingGoogleClickIds(
  href: string = typeof window !== 'undefined' ? window.location.href : '',
): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const url = new URL(href);
    const landing = state.landing?.campaign ? state.landing.touch : null;
    const touch: AttributionTouch = landing && GOOGLE_CLICK_IDS.some((k) => landing[k])
      ? landing
      : (state.attr?.l || {});
    GOOGLE_CLICK_IDS.forEach((key) => {
      const id = clean(touch[key], 512);
      if (!id || url.searchParams.get(key) === id) return;
      if ((readCookie(GCL_COOKIE[key]) || '').includes(id)) return;
      out[key] = id;
    });
  } catch {
    // no URL API: nothing to hand over
  }
  return out;
}

/**
 * Puts `ids` on the address bar's query (replaceState, the router's own
 * state kept) and returns the undo, which takes them off again - only
 * those, and only while they are still there, so a route change made in
 * between is never undone.
 */
function addToUrl(ids: Record<string, string>): () => void {
  const w = win();
  if (!w || !Object.keys(ids).length) return () => undefined;
  const h = w.history;
  try {
    const url = new URL(w.location.href);
    Object.keys(ids).forEach((key) => url.searchParams.set(key, ids[key]));
    h.replaceState(h.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      const now = new URL(w.location.href);
      const ours = Object.keys(ids).filter((key) => now.searchParams.get(key) === ids[key]);
      if (!ours.length) return;
      ours.forEach((key) => now.searchParams.delete(key));
      h.replaceState(h.state, '', `${now.pathname}${now.search}${now.hash}`);
    } catch {
      // the URL keeps the click id until the next route change
    }
  };
}

/** How long the click id may stay on the URL if gtag.js never answers. */
const CLICK_ID_URL_MS = 30000;

/** The page was reached from the marketing site (or this host). */
const fromOwnHost = (): boolean => {
  try {
    return /(^|\.)aperiomobility\.com$/i.test(new URL(document.referrer).hostname);
  } catch {
    return false;
  }
};

let landingHref = '';

/**
 * Where the privacy policy asks first, in Google's region codes (ISO 3166-1
 * country, or ISO 3166-2 subdivision): Quebec, California, the EU / EEA, the
 * UK and Switzerland - the same list as util/portalConsent and the site's
 * consent.js. Used for Consent Mode's regional default.
 */
export const CONSENT_MODE_ASK_FIRST_REGIONS = [
  'CA-QC', 'US-CA',
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO', 'GB', 'CH',
];

const CONSENT_TYPES = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'];
const allConsent = (value: 'granted' | 'denied') => Object.fromEntries(
  CONSENT_TYPES.map((type) => [type, value]),
);

/**
 * Google Consent Mode v2, BASIC (privacy policy: nothing is sent until the
 * visitor's region allows it or they accept, and a decline switches it
 * off): gtag.js is only ever loaded once consent allows a Google tag, so no
 * cookieless ping is sent before a choice or after a refusal. The defaults
 * say what the policy says - denied where it asks first and everywhere
 * under Global Privacy Control, granted elsewhere (implied) - and the
 * update that follows at once says what THIS visitor's decision is, with
 * ad_user_data / ad_personalization following advertising. A refusal later
 * on this page also sets `ga-disable-<id>` (refreshAperioConsent), which
 * stops gtag.js from sending anything more, automatic page views included.
 */
export function consentModeCommands(
  consent: { analytics: boolean; ads: boolean; gpc: boolean },
): Array<[string, string, Record<string, unknown>]> {
  const granted = (on: boolean) => (on ? 'granted' : 'denied');
  const defaults: Array<[string, string, Record<string, unknown>]> = consent.gpc
    ? [['consent', 'default', { ...allConsent('denied'), wait_for_update: 500 }]]
    : [
      ['consent', 'default', { ...allConsent('granted'), wait_for_update: 500 }],
      ['consent', 'default', {
        ...allConsent('denied'),
        region: CONSENT_MODE_ASK_FIRST_REGIONS,
        wait_for_update: 500,
      }],
    ];
  return [
    ...defaults,
    ['consent', 'update', {
      ad_storage: granted(consent.ads),
      ad_user_data: granted(consent.ads),
      ad_personalization: granted(consent.ads),
      analytics_storage: granted(consent.analytics),
    }],
  ];
}

/** `window['ga-disable-<id>']`: gtag.js sends nothing more for that id while true. */
function setGoogleDisabled(ids: AperioTrackingIds, analyticsOff: boolean, adsOff: boolean): void {
  const w = win();
  if (!w) return;
  if (ids.ga4Id) w[`ga-disable-${ids.ga4Id}`] = analyticsOff;
  if (ids.googleAdsId) w[`ga-disable-${ids.googleAdsId}`] = adsOff;
}

function loadGoogle(): void {
  const { ids } = state;
  consentModeCommands({
    analytics: analyticsTagConsent(),
    ads: adConsent(),
    gpc: state.consent.gpc,
  }).forEach((command) => gtag(...command));
  // Analytics without advertising: no ad cookie is read or written, and
  // ad click ids are redacted from what GA4 sends.
  if (!adConsent()) gtag('set', 'ads_data_redaction', true);
  setGoogleDisabled(ids, !analyticsTagConsent(), !adConsent());
  gtag('js', new Date());
  if (ga4On()) {
    // One GA4 session with aperiomobility.com: the two hosts share the _ga
    // cookies, `linker` carries the session on links between them (`_gl`),
    // and a page reached from the site does not count the hop as a referral.
    // The first page_view is sent by hand with the landing's campaign
    // (landingPageLocation), as an event parameter: a page_location in the
    // config would stick to every later page of this single-page app.
    gtag('config', ids.ga4Id, {
      send_page_view: false,
      linker: { domains: LINKER_DOMAINS },
      ...(fromOwnHost() && { ignore_referrer: true }),
      ...ownTrafficParams(),
    });
    // The landing's own campaign keys, also when the visitor accepted on a
    // later step of this page load (the URL no longer has them by then).
    const pageLocation = withLandingCampaign(
      landingPageLocation(landingHref || undefined),
      state.landing,
    );
    // With the booking skin and A/B arm, like every later event
    // (setTrackingContext runs before this: index.tsx).
    gtag('event', 'page_view', {
      send_to: ids.ga4Id,
      ...(pageLocation && { page_location: pageLocation }),
      ...state.context,
      ...ownTrafficParams(),
    });
  }
  if (adsOn()) {
    // The Google click id this visit is known by (the landing in memory,
    // else `apr_attr`'s last touch) when gtag cannot see it: a direct
    // landing that accepts on a later step (the URL lost it with the first
    // route change), or a click recorded before advertising was allowed.
    // gtag's conversion linker reads the click id from the page URL only -
    // a page_location parameter does not make it write _gcl_aw (headless
    // check with the real gtag.js, 2026-10-25) - so the id goes back on the
    // address bar until gtag.js has read it (`get` answers after the
    // config), then comes off again. Only once advertising consent is on
    // (adsOn), never for our own traffic.
    const missing = missingGoogleClickIds();
    const undo = addToUrl(missing);
    gtag('config', ids.googleAdsId, { linker: { domains: LINKER_DOMAINS } });
    if (Object.keys(missing).length) {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        undo();
      };
      gtag('get', ids.googleAdsId, 'gclid', finish);
      setTimeout(finish, CLICK_ID_URL_MS);
    }
  }
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    ga4On() ? ids.ga4Id : ids.googleAdsId,
  )}`);
}

function loadMeta(): void {
  const w = win();
  if (!w) return;
  if (!w.fbq) {
    const fbq: any = function fbqShim(...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    w.fbq = fbq;
    if (!w._fbq) w._fbq = fbq;
  }
  w.fbq('consent', 'grant');
  w.fbq('init', state.ids.metaPixelId);
  w.fbq('track', 'PageView');
  loadScript('https://connect.facebook.net/en_US/fbevents.js');
}

function loadUet(): void {
  const w = win();
  if (!w) return;
  w.uetq = w.uetq || [];
  w.uetq.push('consent', 'default', { ad_storage: 'denied' });
  w.uetq.push('consent', 'update', { ad_storage: 'granted' });
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://bat.bing.com/bat.js';
  script.setAttribute('data-apr-src', script.src);
  script.onload = () => {
    try {
      const queued = w.uetq;
      const UET = w.UET as any;
      if (UET) {
        w.uetq = new UET({
          ti: state.ids.uetTagId,
          enableAutoSpaTracking: true,
          q: queued,
        });
        w.uetq.push('pageLoad');
      }
    } catch {
      // the tag failed to start - conversions are best effort
    }
  };
  appendScript(script);
}

function loadNextdoor(): void {
  const w = win();
  if (!w) return;
  if (!w.ndp) {
    const ndp: any = function ndpShim(...args: unknown[]) {
      if (ndp.handleRequest) ndp.handleRequest(...args);
      else ndp.queue.push(args);
    };
    ndp.queue = [];
    w.ndp = ndp;
  }
  w.ndp('init', state.ids.nextdoorPixelId, {});
  w.ndp('track', NEXTDOOR_EVENTS.pageView);
  // Nextdoor's snippet carries the pixel id on the script URL.
  loadScript(`https://ads.nextdoor.com/public/pixel/ndp.js?id=${encodeURIComponent(
    state.ids.nextdoorPixelId,
  )}`);
}

function loadLinkedin(): void {
  const w = win();
  if (!w) return;
  w._linkedin_partner_id = state.ids.linkedinPartnerId;
  w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
  w._linkedin_data_partner_ids.push(state.ids.linkedinPartnerId);
  if (!w.lintrk) {
    const lintrk: any = function lintrkShim(a: unknown, b: unknown) {
      lintrk.q.push([a, b]);
    };
    lintrk.q = [];
    w.lintrk = lintrk;
  }
  loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

/**
 * OpenAI (ChatGPT) Ads pixel: OpenAI's queue stub and
 * https://bzrcdn.openai.com/sdk/oaiq.min.js, then
 * `oaiq('init', {pixelId, debug})` and a `page_viewed` (the SDK records no
 * page view on its own). Advertising consent only; debug only under the QA
 * flag (which never reaches here: the QA flag keeps every ad tag off, so in
 * practice debug is false - it is set from the flag all the same). Its
 * conversions are aprPurchase's.
 */
function loadOpenai(): void {
  const w = win();
  if (!w) return;
  if (!w.oaiq) {
    const oaiq: any = function oaiqShim(...args: unknown[]) {
      oaiq.q.push(args);
    };
    oaiq.q = [];
    w.oaiq = oaiq;
  }
  w.oaiq('consent', true);
  w.oaiq('init', { pixelId: state.ids.openaiPixelId, debug: state.qa });
  w.oaiq('measure', 'page_viewed', { type: 'contents' });
  loadScript('https://bzrcdn.openai.com/sdk/oaiq.min.js');
}

// ---------------------------------------------------------------- events

export type AperioEventName =
  | 'booking_start'
  | 'address_check_submit'
  | 'address_serviceable'
  | 'address_unserviceable'
  | 'vehicle_identified'
  | 'plan_select'
  | 'appointment_selected'
  | 'appointment_confirmed'
  | 'begin_checkout'
  // The plan step opened: Meta's InitiateCheckout (owner's dev list
  // 2026-11-04, item 2). Once per page load.
  | 'checkout_start'
  | 'phone_click'
  // A free fleet account made on /fleet/signup (owner, 2026-10-28).
  | 'fleet_signup'
  // Consumer skin (owner brief 2026-10-21); GA4 only.
  | 'visit_select'
  | 'car_lookup'
  | 'vehicle_unverified'
  | 'skin_switch';

/**
 * Meta keeps its funnel events (owner, 2026-10-21: "do what's best"): while
 * bookings are few, InitiateCheckout / Contact and the custom steps give
 * Meta's delivery something to learn from. They carry no value and no
 * currency - `Purchase` (aprPurchase) is the only valued event, the one to
 * optimise for - and no personal data: only the skin and A/B arm
 * (metaParams). Same events as before the consumer skin.
 */
const META_STANDARD: Partial<Record<AperioEventName, string>> = {
  // Owner's dev list 2026-11-04, item 2: Lead on the address check,
  // InitiateCheckout when the plan step opens (it used to be the submit,
  // which only people who already booked reached).
  address_serviceable: 'Lead',
  checkout_start: 'InitiateCheckout',
  // A flow without the plan step (the classic skin) still checks out here;
  // after a plan step it is not counted twice (send).
  begin_checkout: 'InitiateCheckout',
  phone_click: 'Contact',
  fleet_signup: 'Lead',
};
/**
 * The Meta events the server also sends (Conversions API, POST
 * /api/ad-event) with the pixel's own event id, so a browser that blocks
 * the pixel still counts and Meta keeps one of the two. The prefix of the id.
 */
const META_RELAY: Partial<Record<AperioEventName, string>> = {
  address_serviceable: 'lead',
  checkout_start: 'ic',
};
const META_CUSTOM: Partial<Record<AperioEventName, string>> = {
  address_serviceable: 'AddressServiceable',
  vehicle_identified: 'VehicleIdentified',
  plan_select: 'PlanSelected',
};
const UET_EVENTS: AperioEventName[] = ['begin_checkout', 'address_serviceable'];
const NEXTDOOR: Partial<Record<AperioEventName, string>> = {
  begin_checkout: NEXTDOOR_EVENTS.begin_checkout,
  address_serviceable: NEXTDOOR_EVENTS.address_serviceable,
  phone_click: NEXTDOOR_EVENTS.phone_click,
};
/**
 * Google Ads: the confirmed booking (`purchase`, aprPurchase) is the primary
 * conversion. Since 2026-10-27 (owner: "send address checked ... call button
 * tapped ... mark them secondary on Google; bookings stay primary") an
 * address checked in a served area and a tapped phone number are sent as
 * well, to conversion actions the server keeps SECONDARY
 * (server/src/modules/ads-sync/google-site-conversions.service.ts): they
 * show in "All conv." and give the platform a signal, but bidding ignores
 * them, so they no longer count visitors who never booked as bookings.
 * begin_checkout stays GA4 / Meta / UET / Nextdoor only.
 */
const ADS_LABEL: Partial<Record<AperioEventName, keyof AperioTrackingIds['googleAdsLabels']>> = {
  address_serviceable: 'address_serviceable',
  phone_click: 'phone_click',
  // The free fleet sign-up counts on the site's "Aperio – Fleet form sent"
  // action (secondary): it is a fleet lead, made in the portal.
  fleet_signup: 'fleet_lead',
};
/**
 * OpenAI (ChatGPT) Ads: the same two micro-conversions as custom events
 * (OpenAI's `custom` event with `custom_event_name`). No value, no personal
 * data. The booking keeps its own events (aprPurchase).
 */
const OPENAI_FUNNEL: Partial<Record<AperioEventName, string>> = {
  address_serviceable: OPENAI_EVENTS.address_checked.name,
  phone_click: OPENAI_EVENTS.call_tapped.name,
};
/** OpenAI standard events from the portal's own forms (the fleet sign-up is a lead). */
const OPENAI_STANDARD: Partial<Record<AperioEventName, { name: string; type: string }>> = {
  fleet_signup: OPENAI_EVENTS.lead_created,
};

const toMajor = (cents: number | null | undefined): number => (
  Math.round(Number(cents) || 0) / 100
);

/** Only value / currency / transaction id ever reach an ad platform. */
const adParams = (params: Params) => {
  const out: Record<string, unknown> = {};
  if (params.value !== undefined && params.value !== null) out.value = params.value;
  if (params.currency) out.currency = params.currency;
  return out;
};

/**
 * Meta's custom data on every portal event: the booking skin and A/B arm
 * (setTrackingContext) and nothing else - no value on a funnel event, never
 * a personal value.
 */
const metaParams = (): Record<string, unknown> => ({ ...state.context });

/**
 * Nextdoor's custom data: `order_value` is the ISO currency immediately
 * followed by the amount (`CAD199.00`), per Nextdoor's own tag template;
 * left out when there is no value.
 */
export function nextdoorOrderValue(
  value: unknown,
  currency: unknown,
): string | undefined {
  const amount = Number(value);
  if (value === undefined || value === null || value === '' || !Number.isFinite(amount)) {
    return undefined;
  }
  return `${String(currency || 'CAD').toUpperCase()}${amount.toFixed(2)}`;
}

/** 20 random letters and digits, for a Meta event id. */
function randomEventId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(20);
  try {
    window.crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

/** How long the server copy waits, so the pixel has set its `_fbp` cookie. */
export const META_RELAY_DELAY_MS = 1500;

/**
 * The server copy of a Meta funnel event (Conversions API): Meta's own
 * browser ids (_fbp, _fbc), the page without its query, and the pixel's
 * event id. Only with advertising consent (metaOn, checked again when it
 * goes), never in QA or from our own devices, never a personal value.
 */
function relayMetaEvent(event: AperioEventName, eventId: string): void {
  const base = String(process.env.REACT_APP_SERVER_URL || '').replace(/\/+$/, '');
  if (!base) return;
  const url = (() => {
    try { return `${window.location.origin}${window.location.pathname}`; } catch { return ''; }
  })();
  setTimeout(() => {
    try {
      if (!metaOn()) return;
      const fbp = clean(readCookie('_fbp'));
      const fbc = metaFbc();
      if (!fbp && !fbc) return;
      fetch(`${base}/api/ad-event`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event, eventId, url, ...(fbp && { fbp }), ...(fbc && { fbc }),
        }),
      }).catch(() => undefined);
    } catch {
      // best effort
    }
  }, META_RELAY_DELAY_MS);
}

function send(name: string, params: Params, event?: AperioEventName): void {
  const w = win();
  if (!w) return;
  if (ga4On()) {
    gtag('event', name, {
      ...params, ...state.context, ...ownTrafficParams(), send_to: state.ids.ga4Id,
    });
  }
  if (!event) return;
  const label = ADS_LABEL[event] && state.ids.googleAdsLabels[ADS_LABEL[event]!];
  if (adsOn() && label) {
    gtag('event', 'conversion', {
      ...adParams(params),
      send_to: `${state.ids.googleAdsId}/${label}`,
    });
  }
  if (metaOn() && w.fbq) {
    const relay = META_RELAY[event];
    if (META_STANDARD[event] && relay) {
      const eventID = `${relay}_${randomEventId()}`;
      w.fbq('track', META_STANDARD[event], metaParams(), { eventID });
      relayMetaEvent(event, eventID);
    } else if (
      META_STANDARD[event]
      && !(event === 'begin_checkout' && state.sent.has('checkout_start:page'))
    ) {
      w.fbq('track', META_STANDARD[event], metaParams());
    }
    if (META_CUSTOM[event]) w.fbq('trackCustom', META_CUSTOM[event], metaParams());
  }
  if (uetOn() && UET_EVENTS.includes(event)) {
    w.uetq = w.uetq || [];
    w.uetq.push('event', name, {
      ...(params.value !== undefined && { revenue_value: params.value }),
      ...(params.currency && { currency: params.currency }),
    });
  }
  if (openaiOn() && w.oaiq && OPENAI_FUNNEL[event]) {
    w.oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: OPENAI_FUNNEL[event] });
  }
  if (openaiOn() && w.oaiq && OPENAI_STANDARD[event]) {
    w.oaiq('measure', OPENAI_STANDARD[event]!.name, { type: OPENAI_STANDARD[event]!.type });
  }
  // ndp('track', NAME, {event_id?}, {order_value?, order_id?}).
  if (nextdoorOn() && w.ndp && NEXTDOOR[event]) {
    const orderValue = nextdoorOrderValue(params.value, params.currency);
    w.ndp('track', NEXTDOOR[event], {}, orderValue ? { order_value: orderValue } : {});
  }
}

/**
 * A contract event. No-op unless the Aperio preset switched tracking on;
 * never throws. `once` sends it at most once per page load for that key.
 */
export function aprTrack(
  event: AperioEventName,
  params: Params = {},
  options: { once?: string } = {},
): void {
  if (!state.enabled) return;
  try {
    if (options.once) {
      const key = `${event}:${options.once}`;
      if (state.sent.has(key)) return;
      state.sent.add(key);
    }
    send(event, params, event);
  } catch {
    // analytics is best effort
  }
}

/**
 * One address check: `address_check_submit` (when the visitor submitted it
 * here, `submit`), then its result, `address_serviceable` or
 * `address_unserviceable` - at most once per address per page load, whichever
 * way it was asked (the automatic check after a pick, "Get Started"). GA4 saw
 * 60 address_serviceable for 30 address_check_submit: the portal reported a
 * result for every hand-off the site had already reported, for addresses
 * restored from an earlier visit, and never sent a submit of its own.
 * `report: false` only marks the address as answered (a hand-off whose
 * result the site already reported), so a later "Get Started" on it stays
 * silent too.
 */
export function aprAddressCheck(
  inService: boolean,
  params: Params,
  options: { key: string; submit: boolean; report?: boolean },
): void {
  if (!state.enabled || !options.key) return;
  const once = `address:${options.key}`;
  if (state.sent.has(once)) return;
  state.sent.add(once);
  if (options.report === false) return;
  if (options.submit) aprTrack('address_check_submit', { src: params.src });
  aprTrack(inService ? 'address_serviceable' : 'address_unserviceable', params);
}

/**
 * phone_click (contract: site + portal) for any `tel:` link the page
 * carries - the Aperio header and footer, the Success page. `cta_id` is
 * `portal_<nav|footer|page>_phone`. One capture-phase listener on the
 * document, installed by initAperioTracking (so only for the Aperio preset);
 * every vendor call inside goes through aprTrack's consent gating.
 */
export function phoneClickHandler(event: Event): void {
  try {
    const target = event.target as Element | null;
    const link = target && typeof target.closest === 'function'
      ? target.closest('a[href^="tel:"]') : null;
    if (!link) return;
    let section = 'page';
    if (link.closest('header')) section = 'nav';
    else if (link.closest('footer')) section = 'footer';
    aprTrack('phone_click', { cta_id: `portal_${section}_phone` });
  } catch {
    // analytics is best effort
  }
}

// ------------------------------------------------------------------ init

/**
 * Switches tracking on for the Aperio preset: reads consent and the
 * attribution cookie, records this landing in `apr_attr` (consent
 * permitting) and loads the vendor tags whose ids are set.
 */
export function initAperioTracking(
  landing: LandingSnapshot | null,
  options: {
    ids?: AperioTrackingIds;
    /** The saved session's roles: a staff account marks the device internal before any tag loads. */
    roles?: unknown;
  } = {},
): void {
  try {
    state.enabled = true;
    if (isStaffRoles(options.roles)) staffSeen = true;
    state.ids = options.ids || APERIO_TRACKING_IDS;
    state.consent = readConsent();
    state.qa = readQaFlag();
    state.internal = readInternalFlag();
    state.landing = landing;
    landingHref = typeof window !== 'undefined' ? window.location.href : '';
    // A re-grant on this page lifts an earlier refusal's gtag switch-off.
    setGoogleDisabled(state.ids, !analyticsTagConsent(), !adConsent());
    const existing = decodeAttr(readCookie(COOKIE_ATTR));
    state.attr = landing ? mergeAttribution(existing, landing) : existing;
    // Only with a decision on file that allows it (readConsent): before the
    // portal bar has learnt the region, the landing stays in memory and is
    // written when the implied grant (or an accept) arrives
    // (refreshAperioConsent runs this again).
    if (state.attr && (state.consent.analytics || state.consent.ads)) {
      writeCookie(COOKIE_ATTR, encodeAttr(state.attr), ATTR_MAX_AGE_S);
    }
    persistInternal();
    // What the visitor declined (on the site, or GPC) is cleaned up here too.
    // No decision yet is not a decline of analytics (as before): only the
    // advertising cookies go until there is one.
    purgeVendorCookies(state.consent.hasCookie || state.consent.gpc
      ? state.consent
      : { ...state.consent, analytics: true });

    if (gtagOn()) loadGoogle();
    if (metaOn()) loadMeta();
    if (uetOn()) loadUet();
    if (nextdoorOn()) loadNextdoor();
    if (linkedinOn()) loadLinkedin();
    if (openaiOn()) loadOpenai();
    if (!phoneListenerInstalled && typeof document !== 'undefined') {
      phoneListenerInstalled = true;
      document.addEventListener('click', phoneClickHandler, true);
    }
  } catch {
    // tracking must never stop the portal from booting
  }
}

/**
 * Tells every tag already on the page to send nothing more: the ad tags
 * always, gtag.js's GA4 too when `analyticsToo`.
 */
function silenceTags(analyticsToo: boolean): void {
  const w = win();
  if (!w) return;
  if (w.dataLayer) {
    gtag('consent', 'update', analyticsToo
      ? allConsent('denied')
      : {
        ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
      });
    setGoogleDisabled(state.ids, analyticsToo, true);
    if (!analyticsToo) gtag('set', 'ads_data_redaction', true);
  }
  if (typeof w.fbq === 'function') w.fbq('consent', 'revoke');
  if (w.uetq && typeof w.uetq.push === 'function') {
    w.uetq.push('consent', 'update', { ad_storage: 'denied' });
  }
  if (typeof w.oaiq === 'function') w.oaiq('consent', false);
}

/**
 * The visitor answered the portal's own consent bar (components/ConsentBar):
 * apply the new `apr_consent` without a reload. A grant loads the tags it
 * allows (initAperioTracking skips the ones already on the page); a decline
 * tells Google so (Consent Mode) and deletes the vendor cookies it denies,
 * and every later aprTrack checks the new consent.
 */
export function refreshAperioConsent(landing: LandingSnapshot | null): void {
  if (!state.enabled) return;
  try {
    state.consent = readConsent();
    if (state.consent.analytics || state.consent.ads) {
      initAperioTracking(landing, { ids: state.ids });
      return;
    }
    purgeVendorCookies(state.consent);
    // A refusal switches the tags off (privacy policy), not just their
    // cookies: Consent Mode denied, and gtag.js / Meta / UET / OpenAI told
    // to send nothing more from this page.
    silenceTags(true);
  } catch {
    // tracking must never break the portal
  }
}

/**
 * A signed-in staff / admin account was seen (util/analytics identifyUser:
 * login, a saved session, the booking's own account): this device is
 * ours. From now on no ad tag receives anything (the ones already on the
 * page are told so), GA4 hits carry traffic_type internal, and the flag is
 * stored once consent allows (readInternalFlag). Returns true when the
 * device is internal (Aperio only; a no-op elsewhere).
 */
export function noteSignedInRoles(roles: unknown): boolean {
  if (!state.enabled || !isStaffRoles(roles)) return false;
  try {
    staffSeen = true;
    if (!state.internal) {
      const adsWereOn = adConsent();
      state.internal = true;
      if (adsWereOn) silenceTags(false);
      if (ga4On()) gtag('set', { traffic_type: 'internal' });
    }
    persistInternal();
  } catch {
    // tracking must never break the portal
  }
  return true;
}

/** booking_start, once per browser session. */
export function aprBookingStart(): void {
  if (!state.enabled) return;
  try {
    if (window.sessionStorage.getItem(BOOKING_START_KEY)) return;
    window.sessionStorage.setItem(BOOKING_START_KEY, '1');
  } catch {
    // no sessionStorage: fall back to once per page load
  }
  aprTrack('booking_start', {}, { once: 'session' });
}

/** First three characters of a postal code, upper case, no spaces. */
export function postal3(postalCode?: string | null): string {
  return String(postalCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
}

/** Currency of a booking address: USD in the US, CAD otherwise. */
export function bookingCurrency(countryCode?: string | null): string {
  return String(countryCode || '').toUpperCase() === 'US' ? 'USD' : 'CAD';
}

export type AperioPlan = 'yearly' | 'monthly' | 'all_inclusive' | 'pay_per_visit';

/**
 * The contract's plan value for a basket: the plan in it, else
 * `pay_per_visit` for a basket with services but no plan, else null.
 */
export function planForBasket(
  nameKeys: Array<string | null | undefined>,
): AperioPlan | null {
  if (!nameKeys.length) return null;
  const kinds = nameKeys.map((key) => aperioPlanKind(key));
  if (kinds.includes('allInclusive')) return 'all_inclusive';
  if (kinds.includes('yearly')) return 'yearly';
  if (kinds.includes('monthly')) return 'monthly';
  return 'pay_per_visit';
}

export interface PurchaseDetails {
  workOrderId: number;
  valueCents: number;
  currency: string;
  plan: AperioPlan | null;
  payment: 'prepaid' | 'at_visit';
  email?: string | null;
  phone?: string | null;
  /** What was booked (names and prices only), for GA4 `items`. */
  items?: PurchaseItem[];
}

export interface PurchaseItem {
  id: string;
  name: string;
  /** Price in cents. */
  priceCents: number;
  category: 'plan' | 'service' | 'add_on';
}

const normaliseEmail = (email?: string | null): string | undefined => {
  const value = String(email || '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : undefined;
};

/**
 * Phone numbers never go to an ad platform (2026-10-28): the privacy policy
 * says mobile numbers are not shared with anyone for marketing, which US
 * texting (Twilio A2P 10DLC) requires. Same switch as the server's
 * conversions.service SEND_PHONE_TO_AD_PLATFORMS.
 */
export const SEND_PHONE_TO_AD_PLATFORMS = false;

/** E.164 for North American numbers (`+14165550100`), else `+<digits>`. */
export function normalisePhone(
  phone?: string | null,
  countryCode?: string | null,
): string | undefined {
  let digits = String(phone || '').replace(/\D/g, '').replace(/^0+/, '');
  if (digits.length === 10 && ['CA', 'US', ''].includes(String(countryCode || '').toUpperCase())) {
    digits = `1${digits}`;
  }
  return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : undefined;
}

/**
 * The purchase, on the success page: GA4 `purchase`, the Google Ads
 * purchase conversion, Meta `Purchase` (eventID `wo_<id>`, the server's
 * CAPI event id, so Meta keeps one), UET and Nextdoor. Enhanced-conversion
 * email / phone go to Google Ads, UET and Meta only with advertising
 * consent. Once per work order, even across a reload.
 */
export function aprPurchase(
  details: PurchaseDetails,
  countryCode?: string | null,
): void {
  if (!state.enabled || !details.workOrderId) return;
  const w = win();
  if (!w) return;
  try {
    // Once per work order, keyed by the id the confirmation page shows: in
    // this page load, across a reload (sessionStorage) and across a tab
    // restored or reopened on the same confirmation (localStorage). QA
    // 2026-09-25 #12: a reload fired a second purchase.
    const key = `${PURCHASE_SENT_KEY}:${details.workOrderId}`;
    if (state.sent.has(key)) return;
    const stores: Storage[] = [];
    try { stores.push(w.sessionStorage); } catch { /* blocked */ }
    try { stores.push(w.localStorage); } catch { /* blocked */ }
    const alreadySent = stores.some((store) => {
      try { return !!store.getItem(key); } catch { return false; }
    });
    state.sent.add(key);
    if (alreadySent) return;
    stores.forEach((store) => {
      try { store.setItem(key, '1'); } catch { /* full or blocked */ }
    });

    const transactionId = `wo_${details.workOrderId}`;
    const value = toMajor(details.valueCents);
    const { currency } = details;
    const email = normaliseEmail(details.email);
    // Never a phone number to an ad platform (server conversions.service
    // SEND_PHONE_TO_AD_PLATFORMS, 2026-10-28: US texting rules).
    const phone = SEND_PHONE_TO_AD_PLATFORMS
      ? normalisePhone(details.phone, countryCode)
      : undefined;
    const items = (details.items || []).map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      price: toMajor(item.priceCents),
      quantity: 1,
    }));

    if (ga4On()) {
      // Our own testing: a qa_purchase GA4 never counts as revenue, so a
      // tester can still see the booking land (DebugView) - never `purchase`.
      gtag('event', state.qa ? 'qa_purchase' : 'purchase', {
        send_to: state.ids.ga4Id,
        transaction_id: transactionId,
        ...(!state.qa && { value, currency }),
        plan: details.plan ?? undefined,
        payment: details.payment,
        ...(items.length && { items }),
        ...state.context,
        ...ownTrafficParams(),
      });
    }
    // OpenAI (ChatGPT) Ads: appointment_scheduled for every booking, and
    // subscription_created as well when it includes a plan; the amount in
    // cents, the booking's currency, the same `wo_<id>` as every other
    // platform. No name, email, phone or address.
    if (openaiOn() && w.oaiq) {
      const amount = Math.round(Number(details.valueCents) || 0);
      const measure = (
        event: { name: string; type: string },
        eventId: string,
      ) => w.oaiq(
        'measure',
        event.name,
        { type: event.type, amount, currency },
        { event_id: eventId },
      );
      measure(OPENAI_EVENTS.appointment_scheduled, transactionId);
      if (details.plan && details.plan !== 'pay_per_visit') {
        measure(OPENAI_EVENTS.subscription_created, `${transactionId}_plan`);
      }
    }
    const label = state.ids.googleAdsLabels.purchase;
    if (adsOn() && label) {
      if (email || phone) {
        gtag('set', 'user_data', {
          ...(email && { email }),
          ...(phone && { phone_number: phone }),
        });
      }
      gtag('event', 'conversion', {
        send_to: `${state.ids.googleAdsId}/${label}`,
        value,
        currency,
        transaction_id: transactionId,
      });
    }
    if (metaOn() && w.fbq) {
      if (email || phone) {
        // Advanced matching: the pixel hashes these before they leave.
        w.fbq('init', state.ids.metaPixelId, {
          ...(email && { em: email }),
          ...(phone && { ph: phone.replace(/\D/g, '') }),
        });
      }
      w.fbq('track', 'Purchase', {
        value, currency, ...metaParams(),
      }, { eventID: transactionId });
    }
    if (uetOn()) {
      w.uetq = w.uetq || [];
      if (email || phone) {
        w.uetq.push('set', {
          pid: { ...(email && { em: email }), ...(phone && { ph: phone }) },
        });
      }
      w.uetq.push('event', 'purchase', { revenue_value: value, currency, event_id: transactionId });
    }
    if (nextdoorOn() && w.ndp) {
      const orderValue = nextdoorOrderValue(value, currency);
      w.ndp('track', NEXTDOOR_EVENTS.purchase, { event_id: transactionId }, {
        order_id: transactionId,
        ...(orderValue && { order_value: orderValue }),
      });
    }
  } catch {
    // analytics is best effort
  }
}

// ----------------------------------------------------------- attribution

/** A first touch, for the people profile (aperioFirstTouch). */
export interface AperioFirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_page?: string;
}

const SUPER_UTM: Array<[keyof AttributionTouch, string]> = [
  ['s', 'utm_source'],
  ['m', 'utm_medium'],
  ['c', 'utm_campaign'],
  ['t', 'utm_term'],
  ['n', 'utm_content'],
];

function touchProperties(touch: AttributionTouch | undefined, prefix: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!touch) return out;
  SUPER_UTM.forEach(([key, name]) => {
    const value = clean(touch[key]);
    if (value) out[prefix + name] = value;
  });
  CLICK_ID_KEYS.forEach((key) => {
    const value = clean(touch[key], 512);
    if (value) out[prefix + key] = value;
  });
  return out;
}

/**
 * The campaign as Mixpanel super properties: the last touch (this landing's
 * utm_* / click ids when it carried any, else `apr_attr`'s last touch, which
 * the marketing site shares on .aperiomobility.com) as `utm_source` …
 * `ndclid`, and the first touch as `initial_utm_source` … `initial_ndclid`.
 * Empty unless tracking is on (Aperio only) and the visitor accepted
 * analytics with no GPC (aperioAnalyticsGranted) - the rule Mixpanel
 * itself runs under on this host.
 */
export function attributionSuperProperties(): Record<string, string> {
  if (!state.enabled) return {};
  try {
    // Mixpanel's own rule on this host: an analytics accept, no GPC.
    if (!aperioAnalyticsGranted()) return {};
    const attr = state.attr || (state.landing
      ? mergeAttribution(null, state.landing) : null);
    if (!attr) return {};
    const last = state.landing?.campaign ? state.landing.touch : attr.l;
    return {
      ...touchProperties(last, ''),
      ...touchProperties(attr.f, 'initial_'),
    };
  } catch {
    return {};
  }
}

/**
 * Our own traffic as Mixpanel super properties: `internal: true` and
 * `traffic_type: 'internal'` for a QA or internal device (still tracked,
 * so boards can filter it out), nothing otherwise (util/analytics
 * registerTrafficType unregisters both then, so a cleared flag does not
 * linger in the shared cookie). Analytics consent only, like Mixpanel.
 */
export function trafficSuperProperties(): Record<string, string | boolean> {
  if (!state.enabled) return {};
  try {
    if (!aperioAnalyticsGranted() || !isOwnTraffic()) return {};
    return { internal: true, traffic_type: 'internal' };
  } catch {
    return {};
  }
}

/**
 * The first touch for the people profile's `$set_once` (util/analytics
 * identifyUser): source / medium / campaign and the landing page (a path,
 * never a query string). Same gate as attributionSuperProperties.
 */
export function aperioFirstTouch(): AperioFirstTouch {
  if (!state.enabled) return {};
  try {
    if (!readConsent().analytics) return {};
    const attr = state.attr || (state.landing
      ? mergeAttribution(null, state.landing) : null);
    const first = attr?.f;
    if (!first) return {};
    const path = clean(first.lp, 512)?.split(/[?#]/)[0];
    return {
      ...(clean(first.s) && { utm_source: clean(first.s) }),
      ...(clean(first.m) && { utm_medium: clean(first.m) }),
      ...(clean(first.c) && { utm_campaign: clean(first.c) }),
      ...(path && { landing_page: path }),
    };
  } catch {
    return {};
  }
}

/**
 * The apr_attr last-touch utm_source, for the coverage log (getPostal), so
 * an address check can be joined to the booking or sign-up that followed.
 * Undefined unless tracking is on (Aperio only) and the visitor allowed
 * analytics or advertising - the same gate as bookingAttributionInput.
 */
export function attributionLastSource(): string | undefined {
  if (!state.enabled) return undefined;
  try {
    const consent = readConsent();
    if (!consent.analytics && !consent.ads) return undefined;
    const attr = state.attr || (state.landing
      ? mergeAttribution(null, state.landing) : null);
    return clean(attr?.l?.s);
  } catch {
    return undefined;
  }
}

/** The ad source fields an address check sends to getPostal. */
export interface CoverageAttribution {
  lastSource: string | null;
  lastMedium: string | null;
  lastCampaign: string | null;
  gclid: string | null;
  fbclid: string | null;
  oppref: string | null;
  referrerHost: string | null;
}

/**
 * The ad source of an address check, for the coverage log (getPostal):
 * the last touch's utm_source / medium / campaign, its click ids (Google,
 * Meta, OpenAI) and referring site (owner, 2026-10-30: "save the ad source
 * on every booking and address check"). Same gate as
 * attributionLastSource: all null unless tracking is on and the visitor
 * allowed analytics or advertising.
 */
export function coverageAttribution(): CoverageAttribution {
  const none: CoverageAttribution = {
    lastSource: null,
    lastMedium: null,
    lastCampaign: null,
    gclid: null,
    fbclid: null,
    oppref: null,
    referrerHost: null,
  };
  if (!state.enabled) return none;
  try {
    const consent = readConsent();
    if (!consent.analytics && !consent.ads) return none;
    const attr = state.attr || (state.landing
      ? mergeAttribution(null, state.landing) : null);
    const first = attr?.f || {};
    const last = attr?.l || {};
    const pick = (key: keyof AttributionTouch) => clean(
      (last as any)[key] ?? (first as any)[key],
      512,
    ) || null;
    return {
      lastSource: clean(last.s) || null,
      lastMedium: clean(last.m) || null,
      lastCampaign: clean(last.c) || null,
      gclid: pick('gclid') || pick('gbraid') || pick('wbraid'),
      fbclid: pick('fbclid'),
      oppref: pick('oppref'),
      referrerHost: clean(last.ref || first.ref) || null,
    };
  } catch {
    return none;
  }
}

/**
 * The `attribution` argument for createWorkOrder, or null when tracking
 * is off (every tenant but Aperio). Campaign data only travels when the
 * visitor allowed analytics or advertising; the ad cookies only with
 * advertising consent, the GA / Mixpanel ids only with analytics consent.
 */
export function bookingAttributionInput(
  mpDeviceId?: string | null,
): BookingAttributionVariables | null {
  if (!state.enabled) return null;
  try {
    const consent = readConsent();
    const out: BookingAttributionVariables = {
      analyticsConsent: consent.analytics,
      adConsent: consent.ads,
      consentState: consentStateOf(consent),
    };
    // No campaign without consent (privacy policy: the campaign cookie and
    // what it remembers are advertising / analytics, and a refusal, GPC or
    // an ask-first region without an accept switch them off). Our own QA
    // flag is not the visitor's campaign (a fixed value a tester sets): it
    // still marks the booking as a test, so no report counts it.
    // Our own device (the internal flag): the server sends no conversion
    // for it. A fixed flag about our own staff, never the visitor's data.
    if (state.internal) out.internal = true;
    if (!consent.analytics && !consent.ads) {
      if (state.qa) out.lastSource = 'qa';
      return out;
    }

    const attr = state.attr || (state.landing
      ? mergeAttribution(null, state.landing) : null);
    const first = attr?.f || {};
    const last = attr?.l || {};
    const pick = (key: keyof AttributionTouch) => clean(
      (last as any)[key] ?? (first as any)[key],
      512,
    );
    Object.assign(out, {
      firstSource: clean(first.s),
      firstMedium: clean(first.m),
      firstCampaign: clean(first.c),
      firstTerm: clean(first.t),
      firstContent: clean(first.n),
      lastSource: clean(last.s),
      lastMedium: clean(last.m),
      lastCampaign: clean(last.c),
      lastTerm: clean(last.t),
      lastContent: clean(last.n),
      gclid: pick('gclid'),
      gbraid: pick('gbraid'),
      wbraid: pick('wbraid'),
      fbclid: pick('fbclid'),
      msclkid: pick('msclkid'),
      ttclid: pick('ttclid'),
      liFatId: pick('li_fat_id'),
      ndclid: pick('ndclid'),
      landingPath: clean(last.lp, 512),
      firstLandingPath: clean(first.lp, 512),
      referrerHost: clean(last.ref || first.ref),
      ctaId: clean(readCookie(COOKIE_CTA), 120),
    });
    const oppref = pick('oppref');
    if (oppref) out.oppref = oppref;
    // Our own testing: the booking says so, and the server sends no
    // conversion for it (ConversionsService.isTestBooking).
    if (state.qa && !QA_SOURCE.test(out.lastSource || '')) out.lastSource = 'qa';
    if (consent.ads) {
      out.fbp = clean(readCookie('_fbp'));
      out.fbc = metaFbc();
    }
    if (consent.analytics) {
      out.gaClientId = gaClientIdFromCookie(readCookie('_ga'));
      out.mpDeviceId = clean(mpDeviceId, 120);
    }
    (Object.keys(out) as Array<keyof BookingAttributionVariables>).forEach((key) => {
      if (out[key] === undefined) delete out[key];
    });
    return out;
  } catch {
    return null;
  }
}
